<template>
  <div class="company-items-content">
    <div class="company-title">{{ title }}</div>
    
    <!-- Display existing companies with blue links -->
    <div v-if="!isEditing && currentItems.length > 0" class="company-list">
      <div v-for="(company, index) in currentItems" :key="index" class="company-item">
        <router-link 
          :to="getCompanyRoute(company)" 
          class="company-link"
          @click.stop
        >
          {{ company.name || company }}
        </router-link>
      </div>
    </div>
    
    <!-- No companies message -->
    <div v-if="!isEditing && currentItems.length === 0" class="no-companies">
      N/A
    </div>
    
    <!-- Edit mode -->
    <div v-if="isEditing" class="edit-mode">
      <div class="editable-items">
        <div v-for="(item, index) in editableItems" :key="index" class="editable-item">
          <div class="autocomplete-container">
            <input
              v-model="item.searchTerm"
              @input="onSearchInput(index)"
              @focus="onSearchFocus(index)"
              @blur="onSearchBlur(index)"
              :placeholder="`Enter ${getSingularTitle()} name...`"
              class="company-input"
            />
            
            <!-- Autocomplete dropdown -->
            <div v-if="item.showDropdown && item.searchResults.length > 0" class="autocomplete-dropdown">
              <div
                v-for="result in item.searchResults.slice(0, 8)"
                :key="result.id || result.name"
                class="autocomplete-item"
                @mousedown="selectCompany(index, result)"
              >
                <div class="company-name">{{ result.name }}</div>
                <div v-if="result.address_line_1" class="company-address">{{ result.address_line_1 }}</div>
              </div>
            </div>
          </div>
          
          <button @click="removeItem(index)" class="remove-btn" type="button">×</button>
        </div>
      </div>
      
      <!-- Add new item button -->
      <button @click="addNewItem" class="add-btn" type="button">+ Add {{ getSingularTitle() }}</button>
      
      <!-- Save/Cancel buttons -->
      <div class="action-buttons">
        <button @click="cancelEditing" class="cancel-btn">Cancel</button>
        <button @click="saveChanges" class="save-btn">Save</button>
      </div>
    </div>
    
    <!-- Edit button (when not editing) -->
    <button v-if="!isEditing" @click="startEditing" class="edit-btn">✏️</button>
  </div>
</template>

<script setup>
import { ref, computed, watch, toRaw } from 'vue'
import { useRouter } from 'vue-router'
import { useDropdownStore } from '@/stores/dropdown_store'
import { useAuthStore } from '@/stores/auth_store'
import axios from 'axios'

const router = useRouter()
const dropdownStore = useDropdownStore()
const authStore = useAuthStore()

// API base URL - use env var so it routes through Nginx (avoids mixed content / port issues)
const API_BASE = process.env.VUE_APP_ENV_API_BASE || 'http://localhost:3000/api/v1/'

// Props
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  items: {
    type: Array,
    default: () => []
  },
  projectId: {
    type: [String, Number],
    required: true
  }
})

// Emits
const emit = defineEmits(['updated'])

// API Base URL
const apiBase = process.env.VUE_APP_ENV_ROOT_BASE || 'http://localhost:3000/api/v1/'

// Reactive data
const isEditing = ref(false)
const editableItems = ref([])
const removedItems = ref([]) // Track items marked for deletion

// Computed properties
const currentItems = computed(() => props.items || [])

// Cache for InvestmentRole IDs resolved by code
const roleIdCache = {
  Lender: null,
  Investor: null,
  Developer: null
}

async function fetchRoleIdByCode(code) {
  console.log(`🔍 Fetching role ID for code: ${code}`)
  if (roleIdCache[code]) {
    console.log(`✅ Found cached role ID for ${code}: ${roleIdCache[code]}`)
    return roleIdCache[code]
  }
  try {
    const authHeader = authStore.getAuthTokenHeader
    // Generic roles index; filter client-side by code
    const resp = await axios.get(`${API_BASE}investment_roles`, { headers: authHeader })
    const list = Array.isArray(resp.data) ? resp.data : []
    console.log(`📋 Available roles:`, list.map(r => ({ id: r.id, code: r.code })))
    const match = list.find(r => r.code === code)
    if (match && match.id) {
      roleIdCache[code] = match.id
      console.log(`✅ Found role ID for ${code}: ${match.id}`)
      return match.id
    }
    console.log(`⚠️ No match found for code: ${code}`)
  } catch (e) {
    console.error('❌ Error fetching InvestmentRole IDs:', e)
  }
  // Fallback to correct hardcoded IDs if API fails
  const roleMapping = {
    'Lender': 205,
    'Investor': 206, 
    'Developer': 207
  }
  const fallbackId = roleMapping[code] || 205
  console.log(`🔄 Using fallback role ID for ${code}: ${fallbackId}`)
  return fallbackId
}

// Company type mappings for APIs and routes
const COMPANY_TYPE_CONFIG = {
  // Capital types
  'Debt': {
    apiEndpoint: 'investor_sponsors',
    routeType: 'investor_sponsors',
    singularTitle: 'Lender',
    linkingEndpoint: 'owner_allocations',
    linkingData: async (companyId, projectId, companyName) => ({
      owner_allocation: {
        projectable_type: 'Project',
        projectable_id: projectId,
        investor_sponsor_id: companyId,
        investment_role_id: await fetchRoleIdByCode('Lender'),
        company_name: companyName
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}owner_allocations/unlink?project_id=${projectId}&investor_sponsor_id=${companyId}`
    }
  },
  'Equity': {
    apiEndpoint: 'investor_sponsors', 
    routeType: 'investor_sponsors',
    singularTitle: 'Investor',
    linkingEndpoint: 'owner_allocations',
    linkingData: async (companyId, projectId, companyName) => ({
      owner_allocation: {
        projectable_type: 'Project',
        projectable_id: projectId,
        investor_sponsor_id: companyId,
        investment_role_id: await fetchRoleIdByCode('Investor'),
        company_name: companyName
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}owner_allocations/unlink?project_id=${projectId}&investor_sponsor_id=${companyId}`
    }
  },
  'Developer(s)': {
    apiEndpoint: 'investor_sponsors',
    routeType: 'investor_sponsors', 
    singularTitle: 'Developer',
    linkingEndpoint: 'owner_allocations',
    linkingData: async (companyId, projectId, companyName) => ({
      owner_allocation: {
        projectable_type: 'Project',
        projectable_id: projectId,
        investor_sponsor_id: companyId,
        investment_role_id: await fetchRoleIdByCode('Developer'),
        company_name: companyName
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}owner_allocations/unlink?project_id=${projectId}&investor_sponsor_id=${companyId}`
    }
  },
  
  // Consultant types
  'Design Architect': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Design Architect',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 354
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Interior Design': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Interior Designer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 361
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Civil Engineer': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Civil Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 355
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Structural Engineer': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Structural Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 356
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'MEP Engineer': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'MEP Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 357
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Mechanical': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Mechanical Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 357
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Electrical': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Electrical Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 358
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Plumbing': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Plumbing Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 359
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Fire Protection': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Fire Protection Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 360
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Environmental': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Environmental Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 364
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Geotechnical Engineer': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Geotechnical Engineer',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 363
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  
  // Others
  "Owner's Representative": {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: "Owner's Representative",
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 351
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Architect of Record': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Architect of Record',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 353 // Architect of Record type ID
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  },
  'Landscape Architect': {
    apiEndpoint: 'consultants',
    routeType: 'consultants',
    singularTitle: 'Landscape Architect',
    linkingEndpoint: 'consultant_projects',
    linkingData: (companyId, projectId) => ({
      consultant_project: {
        project_id: projectId,
        consultant_id: companyId,
        consultant_type_id: 365 // Landscape Architect type ID
      }
    }),
    unlinkEndpoint: (projectId, companyId) => {
      return `${API_BASE}consultant_projects/unlink?project_id=${projectId}&consultant_id=${companyId}`
    }
  }
}

// Methods
function getCompanyConfig() {
  return COMPANY_TYPE_CONFIG[props.title] || {}
}

function getSingularTitle() {
  return getCompanyConfig().singularTitle || props.title
}

function getCompanyRoute(company) {
  const config = getCompanyConfig()
  
  // Unwrap Vue proxy to access properties
  const unwrappedCompany = toRaw(company)
  
  // For investor_sponsors (debt/equity/developers), the primary key is 'id'
  // For consultants, the primary key is 'id' 
  // For contractors, the primary key is 'id'
  // Database structure: investor_sponsors.id, consultants.id, contractors.id
  const companyId = unwrappedCompany.id
  
  if (companyId && config.routeType) {
    return `/companies/${config.routeType}/${companyId}`
  }
  
  console.warn('⚠️ No ID found for company:', company, 'Expected field: id')
  return '#'
}

function startEditing() {
  isEditing.value = true
  
  // Initialize editable items based on current items
  editableItems.value = currentItems.value.map(item => ({
    searchTerm: item.name || item,
    selectedCompany: typeof item === 'object' ? item : null,
    searchResults: [],
    showDropdown: false
  }))
  
  // Add one empty item if none exist
  if (editableItems.value.length === 0) {
    addNewItem()
  }
}

function addNewItem() {
  editableItems.value.push({
    searchTerm: '',
    selectedCompany: null,
    searchResults: [],
    showDropdown: false
  })
}

function removeItem(index) {
  const itemToRemove = editableItems.value[index]
  if (itemToRemove && itemToRemove.selectedCompany && itemToRemove.selectedCompany.id) {
    console.log(`🗑️ Marking for deletion: ${itemToRemove.selectedCompany.name} (ID: ${itemToRemove.selectedCompany.id})`)
    removedItems.value.push(itemToRemove.selectedCompany)
  }
  editableItems.value.splice(index, 1)
}

function cancelEditing() {
  isEditing.value = false
  editableItems.value = []
  removedItems.value = [] // Clear removed items tracking
}

async function onSearchInput(index) {
  const item = editableItems.value[index]
  const searchTerm = item.searchTerm.trim()
  
  if (searchTerm.length < 2) {
    item.searchResults = []
    item.showDropdown = false
    return
  }
  
  try {
    const config = getCompanyConfig()
    if (!config.apiEndpoint) {
      console.warn(`No API endpoint configured for ${props.title}`)
      return
    }
    
    const authToken = authStore.getAuthTokenHeader
    const searchUrl = `${API_BASE}${config.apiEndpoint}`
    console.log(`🔍 ${props.title} search for "${searchTerm}": calling ${searchUrl}`)
    
    const response = await axios.get(
      searchUrl,
      { 
        headers: authToken,
        params: { search: searchTerm }
      }
    )
    
    console.log(`🔍 ${props.title} search for "${searchTerm}": ${response.data?.length || 0} results`)
    console.log(`🔍 First 3 results:`, response.data?.slice(0, 3))
    
    item.searchResults = response.data || []
    item.showDropdown = true
  } catch (error) {
    console.error('Error searching companies:', error)
    item.searchResults = []
    item.showDropdown = false
  }
}

function onSearchFocus(index) {
  const item = editableItems.value[index]
  if (item.searchResults.length > 0) {
    item.showDropdown = true
  }
}

function onSearchBlur(index) {
  // Delay hiding dropdown to allow clicks
  setTimeout(() => {
    const item = editableItems.value[index]
    if (item) {
      item.showDropdown = false
    }
  }, 200)
}

function selectCompany(index, company) {
  const item = editableItems.value[index]
  item.selectedCompany = company
  item.searchTerm = company.name
  item.showDropdown = false
}

async function saveChanges() {
  try {
    console.log(`💾 Saving changes for ${props.title}`)
    
    // Filter out empty items
    const validItems = editableItems.value.filter(item => item.searchTerm.trim() !== '')
    
    const config = getCompanyConfig()
    if (!config.apiEndpoint) {
      console.error(`No API configuration for ${props.title}`)
      return
    }
    
    console.log(`🔍 Processing ${validItems.length} companies for ${props.title}`)
    console.log(`🗑️ Processing ${removedItems.value.length} removals for ${props.title}`)
    
    // 🔥 FIRST: Delete removed items (unlink from project)
    for (const removedCompany of removedItems.value) {
      try {
        if (config.unlinkEndpoint) {
          console.log(`🗑️ Unlinking ${removedCompany.name} (ID: ${removedCompany.id}) from project ${props.projectId}`)
          const unlinkUrl = config.unlinkEndpoint(props.projectId, removedCompany.id)
          console.log(`🗑️ DELETE ${unlinkUrl}`)
          await axios.delete(
            unlinkUrl,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              }
            }
          )
          console.log(`✅ Successfully unlinked ${removedCompany.name}`)
        }
      } catch (error) {
        console.error(`❌ Error unlinking ${removedCompany.name}:`, error)
        console.error('❌ Full error:', error.response?.data || error.message)
      }
    }
    // Clear removed items after processing
    removedItems.value = []
    
    // Emit update immediately for UI reactivity (optimistic update)
    const optimisticCompanies = validItems.map(item => ({
      id: item.selectedCompany?.id || Date.now() + Math.random(), // Temporary ID for new companies
      name: item.searchTerm,
      ...item.selectedCompany
    }))
    
    emit('updated', { type: props.title, companies: optimisticCompanies })
    console.log(`⚡ Optimistic update emitted for ${props.title}:`, optimisticCompanies)
    
    // Save each company to database
    const savedCompanies = []
    
    for (const item of validItems) {
      try {
        let company = item.selectedCompany
        
        // Create company if it doesn't exist
        if (!company) {
          console.log(`📝 Creating new company: ${item.searchTerm}`)
          const createResponse = await axios.post(
            `${API_BASE}${config.apiEndpoint}`,
            { 
              [config.apiEndpoint.slice(0, -1)]: { name: item.searchTerm } // Remove 's' for singular
            },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              }
            }
          )
          company = createResponse.data
          console.log(`✅ Created company:`, company)
        }
        
        // Link company to project if linking endpoint exists
        if (config.linkingEndpoint && config.linkingData) {
          console.log(`🔗 Linking company to project: ${company.id} → ${props.projectId}`)
          const payload = await config.linkingData(company.id, props.projectId, company.name)
          console.log(`🔗 Payload for linking:`, payload)
          
          const linkResponse = await axios.post(
            `${API_BASE}${config.linkingEndpoint}`,
            payload,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              }
            }
          )
          console.log(`✅ Linked company to project:`, linkResponse.status)
        }
        
        savedCompanies.push(company)
      } catch (error) {
        console.error(`❌ Error saving company ${item.searchTerm}:`, error)
        console.error('❌ Full error details:', error.response?.data || error.message)
        // Continue with other companies
      }
    }
    
    // Final update with actual saved data
    emit('updated', { type: props.title, companies: savedCompanies })
    isEditing.value = false
    console.log(`✅ ${props.title} saved successfully with ${savedCompanies.length} companies`)
    
  } catch (error) {
    console.error(`❌ Error in saveChanges for ${props.title}:`, error)
    console.error('❌ Full error details:', error.response?.data || error.message)
    // Still close editing mode to prevent getting stuck
    isEditing.value = false
  }
}

// Watch for external changes to items
watch(() => props.items, (newItems) => {
  if (!isEditing.value) {
    // Update display when parent data changes
  }
}, { deep: true })
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.company-items-content {
  position: relative;
  min-height: 24px;
  
  .company-title {
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }
}

.company-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.company-item {
  display: flex;
  align-items: center;
}

.company-link {
  color: #4A5FD5;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #3949AB;
  }
}

.no-companies {
  color: #999;
  font-style: italic;
}

.edit-btn {
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 14px;
  color: #666;
  padding: 2px 4px;
  border-radius: 3px;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
}

.company-items-content:hover .edit-btn {
  opacity: 1;
}

.edit-mode {
  .editable-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .editable-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }
  
  .autocomplete-container {
    position: relative;
    flex: 1;
  }
  
  .company-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s ease;
    
    &:focus {
      border-color: #4A5FD5;
    }
  }
  
  .autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 40px;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 600px;
    overflow-y: auto;
  }
  
  .autocomplete-item {
    padding: 14px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background-color: #f8f9fa;
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    .company-name {
      font-weight: 500;
      color: #333;
      font-size: 15px;
    }
    
    .company-address {
      font-size: 13px;
      color: #666;
      margin-top: 4px;
    }
  }
  
  .remove-btn {
    background: #ff4444;
    color: white;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    opacity: 1;
    visibility: visible;
    position: relative;
    z-index: 1001;
    
    &:hover {
      background: #cc0000;
      transform: scale(1.1);
    }
  }
  
  .add-btn {
    background: #4A5FD5;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    margin-bottom: 12px;
    
    &:hover {
      background: #3949AB;
    }
  }
  
  .action-buttons {
    display: flex;
    gap: 8px;
    
    .cancel-btn, .save-btn {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
    }
    
    .cancel-btn {
      background: #f5f5f5;
      color: #666;
      
      &:hover {
        background: #e0e0e0;
      }
    }
    
    .save-btn {
      background: #4CAF50;
      color: white;
      
      &:hover {
        background: #45a049;
      }
    }
  }
}
</style>
